import { useStyle } from "./style";
import { useState } from "react";
import { AgentActions, useAgentStore } from "@/common/stores/persistingStores/agentPermissions";
import { changePresence } from "@/repository/queryFns";
import { AvatarView } from "@/components/atoms/avatar/AvatarView";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Lockr } from "@/common/Lockr";
import { PresenceMenu } from "../../molecules/presenceMenu/PresenceMenu";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import {
	OnOpenChangeData,
	OpenPopoverEvents,
} from "@fluentui/react-components";
import { PresenceState } from "@/common/constants/presence";
import { UserPresenceProps } from "./types";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";

export const UserPresence = (props: UserPresenceProps) => {
	const style = useStyle();
	const setPresence = useAgentStore(
		(actions: AgentActions) => actions.setPresence
	);
	const queueIds =
		props.queueIds ??
		useQueuesStore((state: QueuesState) => state.queues.map((x) => x.id));
	const [presenceMenuOpen, setPresenceMenuOpen] = useState(false);

	const changePresenceHandler = (
		newAvailability: string,
		newActivity: string
	): any => {
		let presenceChangeModel = {
			presence: newAvailability,
			note: newActivity,
			id: props.avatarView.agentState.id,
			changedBy: Lockr.get(StorageKeys.UserId),
		};

		addBreadcrumb(
			"ui_interaction",
			"Changing presence for agent",
			presenceChangeModel
		);

		togglePresenceMenuOpening();

		changePresence(presenceChangeModel)
			.then((_: any) => {
				const currentUserId = Lockr.get(StorageKeys.UserId);

				if (currentUserId === presenceChangeModel.id) {
					setPresence(newAvailability, newActivity);
				}
			})
			.catch((err: any) => {
				captureError(err, "Error on user presence change");
				alert("Error: " + err);
			});
	};

	// AI agents have presence disabled, but supervisors must still be able to reach the
	// refresh action. In that case we open the menu showing only the additional actions.
	const showActionsOnly =
		!!props.isDisabled &&
		!!props.allowActionsWhenDisabled &&
		!!props.additionalActions?.length;

	const togglePresenceMenuOpening = (event?: React.MouseEvent<HTMLElement>) => {
		event?.stopPropagation();
		if (!showActionsOnly && props.avatarView.agentState.activity === PresenceState.Offline) {
			return;
		}

		setPresenceMenuOpen(!presenceMenuOpen);
	};

	const handleOpenChange = (_e: OpenPopoverEvents, data: OnOpenChangeData) => {
		if (!showActionsOnly && props.avatarView.agentState.activity === PresenceState.Offline) {
			return;
		}

		setPresenceMenuOpen(data.open || false);
	};

	const presenceMenu = () => {
		if (props.isDisabled && !showActionsOnly) {
			return (
				<div className={style.userStatus} onClick={togglePresenceMenuOpening}>
					<AvatarView
						agentState={props.avatarView.agentState}
						size={props.avatarView.size}
						className={props.avatarView.className ?? ""}
						photoId={props.avatarView.photoId}
					/>
				</div>
			);
		}

		return (
			<PresenceMenu
				open={presenceMenuOpen}
				hidePresenceOptions={showActionsOnly}
				trigger={
					<div className={style.userStatus} onClick={togglePresenceMenuOpening}>
						<AvatarView
							agentState={props.avatarView.agentState}
							size={props.avatarView.size}
							className={props.avatarView.className ?? ""}
							photoId={props.avatarView.photoId}
						/>
					</div>
				}
				positioning={{
					position: "below",
					offset: { mainAxis: 5, crossAxis: 78 },
				}}
				queueIds={queueIds}
				onChange={changePresenceHandler}
				onOpenChange={handleOpenChange}
				additionalActions={props.additionalActions}
				agentState={props.avatarView.agentState}
				hideLock={props.hideLock}
				hideReasons={props.hideReasons}
			/>
		);
	};

	return presenceMenu();
};
